import {useCallback} from 'react';
import {useSelector} from 'react-redux';
import {generatePath, useParams} from 'react-router-dom';
import {selectSelectedFacilityId} from 'pages/facilities/store/facilities-selectors';

/** @module hooks/useGenerateRoute */

/**
 * Custom hook to generate a route with parameters. Each function returned by this hook can be configured to include the default parameters from the current route.
 *
 * @memberof module:hooks/useGenerateRoute
 *
 * @example
 * const {generateRouteWithParams, generateRouteWithSelectedFacilityId} = useGenerateRoute();
 * const route1 = generateRouteWithSelectedFacilityId('/some-route/:facilityId/nested-path/:tabId', {tabId: tab-1});
 * const route2 = generateRouteWithParams('/some-route/:id', {id: 1});
 *
 * console.log(route1) -> '/some-route/1/nested-path/tab-1'
 * console.log(route2) -> '/some-route/1'
 */
const useGenerateRoute = () => {
    const selectedFacilityId = useSelector(selectSelectedFacilityId) || 0;
    const defaultParams = useParams();

    /**
     * Generate a route with parameters.
     *
     * @param {string} route - The route to generate.
     * @param {Object} params - The parameters to include in the route.
     * @param {boolean} includeDefaultParams - Whether to include default parameters from the current route.
     * @returns {string} The generated route with parameters.
     *
     * @example
     * const route = generateRouteWithParams('/some-route/:id', {id: 1});
     *
     * console.log(route) -> '/some-route/1'
     */
    const generateRouteWithParams = useCallback(
        (route, params = {}, includeDefaultParams = false) =>
            generatePath(route, {
                ...(includeDefaultParams && {...defaultParams}),
                ...params,
            }),
        [defaultParams]
    );

    /**
     * Generates a route using the selected facility ID and additional parameters.
     *
     * @param {string} route - The route to generate.
     * @param {Object} params - Additional parameters to include in the route.
     * @param {boolean} includeDefaultParams - Whether to include default parameters in the route.
     * @returns {string} - The generated route.
     *
     * @example
     * const route = generateRouteWithSelectedFacilityId('/some-route/:facilityId/nested-path/:tabId', {tabId: tab-1});
     *
     * console.log(route) -> '/some-route/1/nested-path/tab-1'
     */
    const generateRouteWithSelectedFacilityId = useCallback(
        (route, params = {}, includeDefaultParams = true) => {
            return generatePath(route, {
                facilityId: selectedFacilityId || 0,
                ...(includeDefaultParams && {...defaultParams}),
                ...params,
            });
        },
        [defaultParams, selectedFacilityId]
    );

    return {
        generateRouteWithSelectedFacilityId,
        generateRouteWithParams,
    };
};

export default useGenerateRoute;
